import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { useEffect, useRef } from "react";
import { Camera, X } from "lucide-react";

interface CameraViewProps {
  videoRef: React.RefObject<HTMLVideoElement>;
  onCapture: (blob: Blob) => void;
  onCancel: () => void;
}

const CameraView = ({ videoRef, onCapture, onCancel }: CameraViewProps) => {
  const { t } = useTranslation();
  const streamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    const initCamera = async () => {
      try {
        console.log('Iniciando cámara...');
        if (!videoRef.current) {
          console.error('No se encontró la referencia del video');
          return;
        }

        const constraints = {
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1280 },
            height: { ideal: 720 },
            frameRate: { ideal: 30 }
          },
          audio: false
        };

        console.log('Solicitando acceso a la cámara con configuración:', constraints);
        const stream = await navigator.mediaDevices.getUserMedia(constraints);

        console.log('Stream obtenido:', {
          tracks: stream.getTracks().length,
          settings: stream.getVideoTracks()[0].getSettings()
        });

        streamRef.current = stream;
        videoRef.current.srcObject = stream;
        
        await new Promise((resolve) => {
          if (videoRef.current) {
            videoRef.current.onloadedmetadata = () => resolve(true);
          }
        });

        await videoRef.current.play();
        console.log('Cámara iniciada correctamente');
      } catch (error) {
        console.error('Error al iniciar la cámara:', error);
      }
    };

    initCamera();

    return () => {
      console.log('Limpiando recursos de la cámara...');
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => {
          track.stop();
          console.log('Track detenido:', track.label);
        });
      }
    };
  }, []);

  const handleCapture = () => {
    if (!videoRef.current) {
      console.error('No se encontró la referencia del video para captura');
      return;
    }

    console.log('Capturando imagen...');
    
    try {
      const canvas = document.createElement('canvas');
      const video = videoRef.current;
      
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      console.log('Dimensiones de captura:', {
        width: canvas.width,
        height: canvas.height,
        videoWidth: video.videoWidth,
        videoHeight: video.videoHeight
      });

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        console.error('No se pudo obtener el contexto del canvas');
        return;
      }
      
      ctx.drawImage(video, 0, 0);
      
      canvas.toBlob(
        (blob) => {
          if (blob) {
            console.log('Imagen capturada exitosamente:', {
              size: blob.size,
              type: blob.type
            });
            onCapture(blob);
          } else {
            console.error('Error al crear el blob de la imagen');
          }
        },
        'image/jpeg',
        0.8
      );
    } catch (error) {
      console.error('Error al capturar la imagen:', error);
    }
  };

  return (
    <div className="relative w-full h-[65vh] bg-black">
      <div className="absolute inset-0 bg-black/60 z-10">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="relative w-[85%] h-[55%] overflow-hidden">
            <div className="absolute inset-0 border-2 border-amber-400 rounded-xl">
              <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-amber-400" />
              <div className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-amber-400" />
              <div className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-amber-400" />
              <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-amber-400" />
            </div>
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-amber-400 opacity-80 animate-scan" />
          </div>
        </div>
      </div>

      <video
        ref={videoRef}
        autoPlay
        playsInline
        className="w-full h-full object-cover"
      />
      
      <div className="absolute bottom-6 left-0 right-0 flex justify-center gap-4 px-6 z-20">
        <Button
          onClick={handleCapture}
          className="flex-1 max-w-[180px] bg-amber-600 hover:bg-amber-700 text-white rounded-full h-12 text-base font-medium shadow-lg transition-colors"
        >
          <Camera className="w-5 h-5 mr-2" />
          {t("products.capture")}
        </Button>
        <Button
          variant="outline"
          onClick={onCancel}
          className="flex-1 max-w-[180px] bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white border-white/20 rounded-full h-12 text-base font-medium shadow-lg transition-colors"
        >
          <X className="w-5 h-5 mr-2" />
          {t("common.cancel")}
        </Button>
      </div>
    </div>
  );
};

export default CameraView;
